import React, { ReactNode, useMemo } from 'react';
import { notification } from 'antd';
import { useClassnames } from '@wb-tech/ui-react';

import { Message, TypedIcon, CloseIcon, ExtraMessage } from '#hook/components/notification';

import style from './components/notification/index.module.pcss';

const DEFAULT_DURATION = 10;

export interface IError {
    data: string,
    error: string,
    originalStatus: number,
    status: string
}

export const typeGuardErrorData = (error: unknown): error is IError => {
    return !!error && typeof error === 'object' && 'data' in error;
};

export const getErrorData = (error: unknown): string => {
    return typeGuardErrorData(error) ? String(error.data) : '';
};

export const useNotification = () => {
    const cn = useClassnames(style);

    const methods = useMemo(() => ({
        error: (content: ReactNode, duration?: number, btn?: JSX.Element, key?: string) => {
            const type = 'error';

            void notification.error({
                message  : <Message content={content} />,
                type,
                duration : duration || DEFAULT_DURATION,
                icon     : <TypedIcon type={type} />,
                closeIcon: <CloseIcon />,
                className: cn('notification_error'),
                btn,
                key
            });
        },
        warning: (content: ReactNode, duration?: number) => {
            const type = 'warning';

            void notification.success({
                message  : <Message content={content} />,
                type,
                duration : duration || DEFAULT_DURATION,
                icon     : <TypedIcon type={type} />,
                closeIcon: <CloseIcon />,
                className: cn('notification_warning')
            });
        },
        success: (content: ReactNode, duration?: number) => {
            const type = 'success';

            void notification.success({
                message  : <Message content={content} />,
                type,
                duration : duration || DEFAULT_DURATION,
                icon     : <TypedIcon type={type} />,
                closeIcon: <CloseIcon />,
                className: cn('notification_success')
            });
        },
        extraNotification: ({
            titleContent,
            mainContent,
            hiddenContent,
            type = 'success',
            duration
        }: {
            titleContent?: ReactNode,
            mainContent?: ReactNode,
            hiddenContent?: ReactNode,
            type?: 'error' | 'warning' | 'success',
            duration?: number
        }) => {
            void notification[type]({
                message: <ExtraMessage
                    mainContent={mainContent}
                    titleContent={titleContent}
                    hiddenContent={hiddenContent}
                />,
                type,
                duration : duration || DEFAULT_DURATION,
                icon     : <TypedIcon type={type} />,
                closeIcon: <CloseIcon />,
                className: cn(`notification_${type}`)
            });
        },
        destroy: (key?: string) => notification.destroy(key)
    }), [cn]);

    return { methods };
};
